import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useArtistOnboardingStatus from '../../../hooks/useArtistOnboardingStatus';
import {
  updateArtistBankInfo,
  updateArtistProfile,
  uploadArtistPhotos,
  uploadArtistProfilePicture,
} from '../../../api/artistSetup';
import {
  SkeletonButton,
  SkeletonLine,
  SkeletonPill,
} from '../../common/Skeleton';
import { BankInfoStep, BrandLogo } from './artistOnboardingGateParts';
import {
  EMPTY_BANK_FORM,
  EMPTY_PROFILE_FORM,
  PROFILE_BIO_LIMIT,
  PROFILE_ONE_LINER_LIMIT,
  appendVariant,
  displayNameForProfile,
  normalizeProfilePayload,
  stepMeta,
} from './artistOnboardingGateUtils';
import '../../../css/artist/onboarding/ArtistOnboardingGate.css';

const InitialLoadingSkeleton = () => (
  <div className="artist-onboarding-gate__initial-skeleton" role="status" aria-live="polite">
    <div className="artist-onboarding-gate__progress" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <div key={`artist-onboarding-progress-skeleton-${index}`} className="artist-onboarding-gate__progress-card">
          <div>
            <SkeletonLine width={34} height={9} />
            <SkeletonLine width={index === 0 ? 68 : 82} height={12} />
          </div>
          <SkeletonPill width={30} height={20} />
        </div>
      ))}
    </div>
    <section className="artist-onboarding-gate__step" aria-hidden="true">
      <div className="artist-onboarding-gate__step-heading is-centered">
        <SkeletonLine width={86} height={11} />
        <SkeletonLine width="58%" height={24} />
        <SkeletonLine width="76%" height={13} />
      </div>
      <div className="artist-onboarding-gate__card">
        <SkeletonLine width="42%" height={14} />
        <SkeletonLine width="94%" height={12} />
        <SkeletonLine width="88%" height={12} />
        <SkeletonButton width="100%" height={44} />
      </div>
    </section>
  </div>
);

const ArtistOnboardingGate = ({ children }) => {
  const {
    loading,
    error,
    required,
    currentStep,
    steps,
    profile,
    bankInfo,
    photoCount,
    answerStep,
    dismissOnboarding,
    refresh,
    refreshAfterMutation,
  } = useArtistOnboardingStatus();
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE_FORM);
  const [bankForm, setBankForm] = useState(EMPTY_BANK_FORM);
  const [profileImageMode, setProfileImageMode] = useState('confirm');
  const [selectedStepKey, setSelectedStepKey] = useState('');
  const [completionVisible, setCompletionVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [promoPreviewUrls, setPromoPreviewUrls] = useState([]);
  const progressRef = useRef(null);
  const progressCardRefs = useRef({});
  const profileFileRef = useRef(null);
  const promoFileRef = useRef(null);
  const promoPreviewUrlsRef = useRef([]);
  const promoAutoAdvanceRef = useRef(false);

  useEffect(() => {
    if (!profile) return;
    setProfileForm({
      profile_one_liner: profile.profile_one_liner || '',
      bio: profile.bio || '',
      bio_small_group: profile.bio_small_group || '',
      bio_large_group: profile.bio_large_group || '',
    });
  }, [profile]);

  useEffect(() => {
    if (!bankInfo) return;
    setBankForm({ ...EMPTY_BANK_FORM, ...bankInfo });
  }, [bankInfo]);

  useEffect(() => {
    setSelectedStepKey('');
  }, [currentStep?.key]);

  useEffect(() => {
    promoPreviewUrlsRef.current = promoPreviewUrls;
  }, [promoPreviewUrls]);

  useEffect(() => () => {
    promoPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const activeStep = currentStep || steps.find((step) => !step.done) || steps[0] || null;
  const visibleStep = selectedStepKey
    ? steps.find((step) => step.key === selectedStepKey) || activeStep
    : activeStep;
  const visibleStepMeta = stepMeta(visibleStep);
  const visibleStepIndex = useMemo(() => {
    const key = visibleStep?.key;
    const index = steps.findIndex((step) => step.key === key);
    return index >= 0 ? index : 0;
  }, [visibleStep?.key, steps]);

  useEffect(() => {
    const container = progressRef.current;
    const activeCard = visibleStep?.key ? progressCardRefs.current[visibleStep.key] : null;
    if (!container || !activeCard) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeCard.getBoundingClientRect();
    const targetLeft = Math.max(0, container.scrollLeft + activeRect.left - containerRect.left);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    container.scrollTo({
      left: targetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [visibleStep?.key, visibleStepIndex, steps.length]);

  const profilePhotoUrl = appendVariant(profile?.profile_picture_url || '', 'medium');
  const profilePhotoData = visibleStep?.key === 'profile_photo' ? visibleStep.data || {} : {};
  const needsProfileUpload = Boolean(profilePhotoData.requiresUpload || !profile?.profile_picture);
  const artistName = displayNameForProfile(profile);
  const [failedProfilePhotoUrl, setFailedProfilePhotoUrl] = useState('');
  useEffect(() => {
    setFailedProfilePhotoUrl('');
  }, [profilePhotoUrl]);
  const visibleProfilePhotoUrl = profilePhotoUrl && failedProfilePhotoUrl !== profilePhotoUrl ? profilePhotoUrl : '';

  useEffect(() => {
    if (visibleStep?.key !== 'profile_photo') return;
    setProfileImageMode(needsProfileUpload ? 'upload' : 'confirm');
  }, [needsProfileUpload, visibleStep?.key]);

  const previousStepKey = steps[visibleStepIndex - 1]?.key || '';
  const stepAfterSkip = useMemo(() => {
    if (!visibleStep?.key) return null;
    return (
      steps
        .slice(visibleStepIndex + 1)
        .find((step) => !step.done && step.key !== visibleStep.key) || null
    );
  }, [steps, visibleStep?.key, visibleStepIndex]);
  const skipButtonLabel = stepAfterSkip?.key ? 'スキップ' : '閉じる';
  const oneLinerLength = String(profileForm.profile_one_liner || '').length;
  const bioLength = String(profileForm.bio || '').length;

  const showCompletedScreen = useCallback((result) => {
    if (result?.required === false) {
      setCompletionVisible(true);
      setSelectedStepKey('');
    }
  }, []);

  const moveToNextIncompleteStepAfter = useCallback((result, stepKey) => {
    if (!result?.required || !stepKey || !Array.isArray(result.steps)) return;
    const completedStepIndex = result.steps.findIndex((step) => step.key === stepKey);
    if (completedStepIndex < 0 || !result.steps[completedStepIndex]?.done) return;
    const nextStep = result.steps
      .slice(completedStepIndex + 1)
      .find((step) => !step.done);
    setSelectedStepKey(nextStep?.key || '');
  }, []);

  const runAction = useCallback(async (fn) => {
    if (working) return;
    const actionStepKey = visibleStep?.key || '';
    setWorking(true);
    setMessage('');
    try {
      const result = await fn();
      showCompletedScreen(result);
      moveToNextIncompleteStepAfter(result, actionStepKey);
    } catch (actionError) {
      setMessage(actionError?.message || '処理に失敗しました');
    } finally {
      setWorking(false);
    }
  }, [moveToNextIncompleteStepAfter, showCompletedScreen, visibleStep?.key, working]);

  const uploadProfilePicture = async (file) => {
    if (!file) throw new Error('画像を選択してください');
    setProfileImageMode('uploading');
    const formData = new FormData();
    formData.append('profilePicture', file);
    try {
      await uploadArtistProfilePicture(formData);
    } catch (error) {
      setProfileImageMode(needsProfileUpload ? 'upload' : 'confirm');
      throw error;
    }
    const status = await refreshAfterMutation();
    setProfileImageMode('confirm');
    return status;
  };

  const uploadPromoPhotos = async (files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) throw new Error('画像を選択してください');

    const previewUrls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPromoPreviewUrls((prev) => [...prev, ...previewUrls].slice(0, 5));

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('photos', file));
    await uploadArtistPhotos(formData);
    return refreshAfterMutation();
  };

  const saveProfile = async () => {
    if (!String(profileForm.bio || '').trim()) {
      throw new Error('紹介プロフィールを入力してください');
    }
    await updateArtistProfile(normalizeProfilePayload(profile || {}, profileForm));
    return refreshAfterMutation();
  };

  const saveBankInfo = async () => {
    const requiredKeys = [
      'bank_name',
      'bank_code',
      'branch_name',
      'branch_code',
      'account_type',
      'account_number',
      'account_name',
    ];
    const missing = requiredKeys.some((key) => !String(bankForm[key] || '').trim());
    if (missing) {
      throw new Error('振込先口座の必須項目を入力してください');
    }
    if (!/^\d{7}$/.test(String(bankForm.account_number || '').trim())) {
      throw new Error('口座番号は7桁で入力してください');
    }
    if (bankForm.invoice_registered) {
      if (!String(bankForm.invoice_company || '').trim()) {
        throw new Error('インボイス事業者名を入力してください');
      }
      if (!String(bankForm.invoice_number || '').trim()) {
        throw new Error('インボイス登録番号を入力してください');
      }
    }
    if (
      bankForm.invoice_sender_mode === 'custom' &&
      String(bankForm.invoice_sender_email || '').trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(bankForm.invoice_sender_email || '').trim())
    ) {
      throw new Error('請求元メールアドレスの形式が正しくありません');
    }
    await updateArtistBankInfo(bankForm);
    return refreshAfterMutation();
  };

  const completePromoPhoto = useCallback(async () => {
    if (photoCount < 1) {
      throw new Error('最低1枚の宣材写真を登録してください');
    }
    return refreshAfterMutation();
  }, [photoCount, refreshAfterMutation]);

  useEffect(() => {
    if (visibleStep?.key !== 'promo_photo' || photoCount < 1) {
      promoAutoAdvanceRef.current = false;
      return;
    }
    if (working || promoAutoAdvanceRef.current) return;
    promoAutoAdvanceRef.current = true;
    runAction(completePromoPhoto);
  }, [completePromoPhoto, photoCount, runAction, visibleStep?.key, working]);

  const answerProfilePhoto = async (answer) => {
    const status = await answerStep('profile_photo', answer);
    if (answer === 'no') {
      setProfileImageMode('upload');
    }
    return status;
  };

  const goToPreviousStep = () => {
    if (!previousStepKey) return;
    setMessage('');
    setSelectedStepKey(previousStepKey);
  };

  const skipCurrentStep = async () => {
    if (working) return;
    setMessage('');
    if (!stepAfterSkip?.key) {
      setWorking(true);
      try {
        await dismissOnboarding();
        setDismissed(true);
      } catch (skipError) {
        setMessage(skipError?.message || 'スキップの保存に失敗しました');
      } finally {
        setWorking(false);
      }
      return;
    }
    setSelectedStepKey(stepAfterSkip.key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const leaveCompletedStep = () => {
    setMessage('');
    setSelectedStepKey('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePromoDrop = (event) => {
    event.preventDefault();
    runAction(() => uploadPromoPhotos(event.dataTransfer.files));
  };

  if (loading) {
    return (
      <div className="artist-onboarding-gate" aria-live="polite">
        <div className="artist-onboarding-gate__container">
          <BrandLogo />
          <InitialLoadingSkeleton />
        </div>
      </div>
    );
  }

  if (dismissed || (!required && !completionVisible)) return children;

  if (completionVisible) {
    return (
      <div className="artist-onboarding-gate" aria-label="初期設定完了">
        <main className="artist-onboarding-gate__container">
          <BrandLogo />
          <section className="artist-onboarding-gate__complete">
            <div className="artist-onboarding-gate__complete-icon">✓</div>
            <h1>初期設定が完了しました</h1>
            <p>プロフィール情報が登録されました。Casticartでオファーを確認できます。</p>
            <button
              type="button"
              className="artist-onboarding-gate__primary"
              onClick={() => setCompletionVisible(false)}
            >
              ダッシュボードへ
            </button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="artist-onboarding-gate" aria-label="初期設定">
      <main className="artist-onboarding-gate__container">
        <BrandLogo />

        <div
          ref={progressRef}
          className="artist-onboarding-gate__progress"
          aria-label="初期設定の進捗"
        >
          {steps.map((step, index) => {
            const meta = stepMeta(step);
            const isCurrent = index === visibleStepIndex;
            const isDone = step.done && !isCurrent;
            return (
              <div
                key={step.key}
                ref={(node) => {
                  if (node) {
                    progressCardRefs.current[step.key] = node;
                  } else {
                    delete progressCardRefs.current[step.key];
                  }
                }}
                className={`artist-onboarding-gate__progress-card ${
                  isCurrent ? 'is-current' : ''
                } ${isDone ? 'is-done' : ''}`}
              >
                <div>
                  <span>STEP {index + 1}</span>
                  <b>{meta.shortTitle || meta.title}</b>
                </div>
                <em aria-hidden="true">{isDone ? '✓' : meta.icon}</em>
              </div>
            );
          })}
        </div>

        {error ? (
          <div className="artist-onboarding-gate__notice is-error">
            <span>{error}</span>
            <button type="button" onClick={() => refresh()} disabled={working}>
              再読み込み
            </button>
          </div>
        ) : null}

        {message ? (
          <div className="artist-onboarding-gate__notice is-error" role="alert">
            <span>{message}</span>
          </div>
        ) : null}

        {visibleStep?.key === 'profile_photo' ? (
          <section className="artist-onboarding-gate__step">
            <div className="artist-onboarding-gate__step-heading is-centered">
              <p>STEP {visibleStepIndex + 1}</p>
              <h1>{visibleStepMeta.title}</h1>
            </div>

            <div className="artist-onboarding-gate__card">
              {profileImageMode === 'uploading' ? (
                <div className="artist-onboarding-gate__processing">
                  <span className="artist-onboarding-gate__spinner" />
                  <b>画像を登録しています</b>
                </div>
              ) : null}

              {profileImageMode === 'upload' ? (
                <div className="artist-onboarding-gate__upload-panel">
                  <h2>プロフィール写真を選択</h2>
                  <p className="artist-onboarding-gate__upload-copy">
                    他のアーティストや運営スタッフへの表示、タクティカートイベント情報公開ページの参加者情報などで利用されます。
                  </p>
                  <input
                    ref={profileFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      runAction(() => uploadProfilePicture(file));
                      event.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="artist-onboarding-gate__dropzone"
                    onClick={() => profileFileRef.current?.click()}
                    disabled={working}
                  >
                    <span>↑</span>
                    <b>クリックしてアップロード</b>
                    <small>JPEG, PNG, WEBP</small>
                  </button>
                  <div className="artist-onboarding-gate__ng-box">
                    <b>NG写真の例</b>
                    <ul>
                      <li>ペット、風景、物だけが写っている写真</li>
                      <li>本人やグループ、活動内容が判別しにくい写真</li>
                      <li>極端に暗い、粗い、顔や姿が大きく隠れている写真</li>
                      <li>権利者の許可がない画像やロゴ画像</li>
                    </ul>
                  </div>
                  {!needsProfileUpload ? (
                    <button
                      type="button"
                      className="artist-onboarding-gate__link-button"
                      onClick={() => setProfileImageMode('confirm')}
                      disabled={working}
                    >
                      戻る
                    </button>
                  ) : null}
                </div>
              ) : null}

              {profileImageMode === 'confirm' ? (
                <div className="artist-onboarding-gate__photo-confirm">
                  <div className="artist-onboarding-gate__avatar-wrap">
                    {visibleProfilePhotoUrl ? (
                      <img
                        src={visibleProfilePhotoUrl}
                        alt="現在のプロフィール画像"
                        onError={() => setFailedProfilePhotoUrl(profilePhotoUrl)}
                      />
                    ) : (
                      <span>{artistName.slice(0, 1)}</span>
                    )}
                    <i aria-hidden="true">✓</i>
                  </div>
                  <h2>これはアーティスト写真ですか？</h2>
                  <p className="artist-onboarding-gate__hint-box">
                    Casticart内で他のアーティストや運営スタッフに表示されるほか、タクティカートイベント情報公開ページで参加者情報として利用される場合があります。
                  </p>
                  <div className="artist-onboarding-gate__ng-box">
                    <b>NG写真の例</b>
                    <ul>
                      <li>ペット、風景、物だけが写っている写真</li>
                      <li>本人やグループ、活動内容が判別しにくい写真</li>
                      <li>極端に暗い、粗い、顔や姿が大きく隠れている写真</li>
                      <li>権利者の許可がない画像やロゴ画像</li>
                    </ul>
                  </div>
                  <div className="artist-onboarding-gate__button-row">
                    <button
                      type="button"
                      className="artist-onboarding-gate__primary"
                      disabled={working}
                      onClick={() =>
                        visibleStep.done
                          ? leaveCompletedStep()
                          : runAction(() => answerProfilePhoto('yes'))
                      }
                    >
                      はい、この画像で進む
                    </button>
                    <button
                      type="button"
                      className="artist-onboarding-gate__secondary"
                      disabled={working}
                      onClick={() =>
                        visibleStep.done
                          ? setProfileImageMode('upload')
                          : runAction(() => answerProfilePhoto('no'))
                      }
                    >
                      画像を変更する
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {visibleStep?.key === 'profile_basic' ? (
          <section className="artist-onboarding-gate__step">
            <div className="artist-onboarding-gate__step-heading">
              <p>STEP {visibleStepIndex + 1}</p>
              <h1>{visibleStepMeta.title}</h1>
              <span>{visibleStepMeta.description}</span>
            </div>

            <div className="artist-onboarding-gate__card">
              <div className="artist-onboarding-gate__account-strip">
                {visibleProfilePhotoUrl ? (
                  <img
                    src={visibleProfilePhotoUrl}
                    alt=""
                    onError={() => setFailedProfilePhotoUrl(profilePhotoUrl)}
                  />
                ) : (
                  <span>{artistName.slice(0, 1)}</span>
                )}
                <div>
                  <small>登録中のアカウント</small>
                  <b>{artistName}</b>
                </div>
              </div>
              <div className="artist-onboarding-gate__form">
                <label>
                  <span>ひとこと</span>
                  <p className="artist-onboarding-gate__field-hint">
                    Casticart内で他のユーザーに向けて表示される短い紹介文です。
                  </p>
                  <input
                    value={profileForm.profile_one_liner}
                    maxLength={PROFILE_ONE_LINER_LIMIT}
                    onChange={(event) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        profile_one_liner: event.target.value,
                      }))
                    }
                    placeholder="例：Casticartではオーケストラを中心に参加しています。"
                  />
                  <div className="artist-onboarding-gate__char-count">
                    {oneLinerLength}/{PROFILE_ONE_LINER_LIMIT}
                  </div>
                </label>
                <label>
                  <span>紹介プロフィール（メイン） <b>*</b></span>
                  <p className="artist-onboarding-gate__field-hint">
                    イベントサイト・フライヤーなどに掲載可能な紹介プロフィールを入力してください。
                  </p>
                  <textarea
                    value={profileForm.bio}
                    maxLength={PROFILE_BIO_LIMIT}
                    required
                    onChange={(event) =>
                      setProfileForm((prev) => ({ ...prev, bio: event.target.value }))
                    }
                    placeholder="出演経歴や自己紹介を入力"
                  />
                  <div className="artist-onboarding-gate__char-count">
                    {bioLength}/{PROFILE_BIO_LIMIT}
                  </div>
                </label>
              </div>
              <div className="artist-onboarding-gate__footer">
                <button
                  type="button"
                  className="artist-onboarding-gate__primary"
                  onClick={() => runAction(saveProfile)}
                  disabled={working}
                >
                  次へ
                </button>
                <button
                  type="button"
                  className="artist-onboarding-gate__back"
                  onClick={goToPreviousStep}
                  disabled={working || !previousStepKey}
                >
                  戻る
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {visibleStep?.key === 'bank_info' ? (
          <BankInfoStep
            visibleStepIndex={visibleStepIndex}
            visibleStepMeta={visibleStepMeta}
            bankForm={bankForm}
            setBankForm={setBankForm}
            runAction={runAction}
            saveBankInfo={saveBankInfo}
            working={working}
            goToPreviousStep={goToPreviousStep}
            previousStepKey={previousStepKey}
          />
        ) : null}

        {visibleStep?.key === 'promo_photo' ? (
          <section className="artist-onboarding-gate__step">
            <div className="artist-onboarding-gate__step-heading">
              <p>STEP {visibleStepIndex + 1}</p>
              <h1>{visibleStepMeta.title}</h1>
              <span>{visibleStepMeta.description}</span>
            </div>

            <div className="artist-onboarding-gate__card">
              <input
                ref={promoFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={(event) => {
                  runAction(() => uploadPromoPhotos(event.target.files));
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                className="artist-onboarding-gate__dropzone"
                onClick={() => promoFileRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handlePromoDrop}
                disabled={working}
              >
                <span>＋</span>
                <b>クリックまたはドラッグ＆ドロップ</b>
                <small>現在の登録枚数: {photoCount}/5 枚</small>
              </button>

              {promoPreviewUrls.length ? (
                <div className="artist-onboarding-gate__photo-grid">
                  {promoPreviewUrls.map((url, index) => (
                    <div key={url}>
                      <img src={url} alt={`選択した宣材写真 ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() =>
                          setPromoPreviewUrls((prev) => {
                            URL.revokeObjectURL(prev[index]);
                            return prev.filter((_, i) => i !== index);
                          })
                        }
                        aria-label="プレビューから削除"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="artist-onboarding-gate__footer">
                <button
                  type="button"
                  className="artist-onboarding-gate__primary"
                  onClick={() => runAction(completePromoPhoto)}
                  disabled={working}
                >
                  完了する
                </button>
                <button
                  type="button"
                  className="artist-onboarding-gate__back"
                  onClick={goToPreviousStep}
                  disabled={working || !previousStepKey}
                >
                  戻る
                </button>
              </div>
            </div>
          </section>
        ) : null}

        <div className="artist-onboarding-gate__skip">
          <button type="button" onClick={skipCurrentStep} disabled={working}>
            {skipButtonLabel}
          </button>
        </div>

        {working ? (
          <div className="artist-onboarding-gate__saving" aria-live="polite">
            <span className="artist-onboarding-gate__spinner" />
            保存しています
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default ArtistOnboardingGate;
